import { IconButton, Stack } from '@mui/material';
import React from 'react';
import { MyDataGrid, MyTypography } from '../../common/components';
import DateTimeUtils from '../../common/services/DateTimeUtils';
import { IconDeviceLandlinePhone } from '@tabler/icons';
import EditIcon from '@mui/icons-material/Edit';
import {common_cols, referralIsOrderColumnItem} from './CommonReferralTable';
import ActiveReferralActionsCell from './components/ActiveReferralActionsCell';
import ActiveReferralStatusCell from './components/ActiveReferralStatusCell';

export default function ActiveSentReferralsTable({
  onFilterChanged,
  onFilterFaxChanged,
  onChangeDropdown,
  onSelectedRowsChanged,
  loading,
  data,
  onClick,
  onViewNotes,
  refresh
}) {

  const renderEditableCell = (params, value) => {
    if (value) return value;
    if (params.row.status !== 'Manually Created') return value;

    return (
      <IconButton
        onClick={e => {
          e.stopPropagation();
          params.row.actions.onUpdateReferral(params.row.referral);
        }}
        size="small"
        style={{ marginLeft: 8 }}
      >
        <EditIcon fontSize="small" />
      </IconButton>
    );
  };

  const updated_cols = common_cols.map(col => ({
    ...col,
    renderCell: params => renderEditableCell(params, col.valueGetter?.(params)),
  }));

  const columns = [
    ...updated_cols,
    {
      field: 'receiver',
      headerName: 'Clinic Receiver',
      width: 250,
      valueGetter: params => {
        return params.row.receiver?.name;
      },
      renderCell: params => {
        if(params.row.receiver?.name) {
          return (
            <Stack direction="row" spacing={1}>
              {(params.row.receiver?.clinic_users_count === 0 && params.row.is_fax) && <IconDeviceLandlinePhone />}
              <MyTypography variant="subtitle1">{params.row.receiver?.name}</MyTypography>
            </Stack>
          )
        }
        return renderEditableCell(params, null);
      },
    },
    {
      field: 'provider',
      headerName: 'Provider',
      width: 160,
      valueGetter: params => {
        return params.row.provider?.display_name;
      },
      renderCell: params => {
        if(params.row.provider?.display_name) {
          return (
            <Stack direction="row" spacing={1}>
              <MyTypography variant="subtitle1">{params.row.provider?.display_name}</MyTypography>
            </Stack>
          )
        }
        return renderEditableCell(params, null);
      },
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 160,
      valueGetter: (params) => params.row.referral.getReferrerStatus(),
      renderCell: (params) => (
        <ActiveReferralStatusCell data={params.row.referral} refresh={refresh}/>
      ),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 300,
      headerAlign: 'center',
      sortable: false,
      editable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params) => <ActiveReferralActionsCell row={params.row} />
    },
    {
      field: 'appointments',
      headerName: 'Appointment Date or Decline Reason',
      width: 300,
      valueGetter: params => {
        return params.row.referral.getDateOrReason();
      },
      renderCell: (params) => {
        const value = params.row.referral.getDateOrReason();
        const referral = params.row.referral?.referral;

        if (
          (referral.appointment != null && referral.status !== 'declined') ||
          (referral.status !== 'draft' && !value)
        ) {
          return (
            <div style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
              <span>{value}</span>
              <IconButton
                onClick={e => {
                  e.stopPropagation();
                  params.row.actions.onSetAppointment(params.row.id, referral.appointment, referral.status);
                }}
                size="small"
                style={{ marginLeft: 8 }}
              >
                <EditIcon fontSize="small" />
              </IconButton>
            </div>
          );
        } else {
          return <span>{value}</span>;
        }
      },
    },
    {
      field: 'sent_at',
      headerName: 'Date Sent',
      width: 250,
      valueGetter: params => {
        return params.row.referral.sentFormattedDate();
      },
      sortComparator: DateTimeUtils.dateSortComparator,
    },
    referralIsOrderColumnItem,
    {
      field: 'updated_by',
      headerName: 'Last Updated By',
      width: 250,
      valueGetter: params => {
        return params.row.referral.getLastUpdatedByFullName();
      },
    },
    {
      field: 'updated_at',
      headerName: 'Last Updated At',
      width: 250,
      valueGetter: params => {
        return params.row.referral.formattedDate();
      },
      sortComparator: DateTimeUtils.dateSortComparator,
    },
  ];

  return (
    <MyDataGrid
      onFilterChanged={onFilterChanged}
      onFilterFaxChanged={onFilterFaxChanged}
      onChangeDropdown={onChangeDropdown}
      onSelectedRowsChanged={onSelectedRowsChanged}
      loading={loading}
      data={data}
      columns={columns}
      onClick={onClick}
      showToolbar={true}
      selections={['By Name', 'By Birthdate']}
      addReferral={true}
      minTableHeight={'calc(100vh - 192px)'}
      getRowId={row => {
        return row.id;
      }}
      fromReceived={false}
    />
  );
}
